/**
 * Phase 6f: Push Notifications Hook
 *
 * Manages service worker registration and push subscription.
 */

import { useState, useEffect, useCallback } from 'react'
import { apiGet, apiPostAuth } from '../api'

interface PushState {
  supported: boolean
  permission: NotificationPermission
  subscribed: boolean
  loading: boolean
  error: string | null
}

interface VapidResponse {
  configured: boolean
  public_key: string | null
}

interface AlertPreferences {
  lineup_alerts: boolean
  builder_alerts: boolean
  goal_alerts: boolean
  card_alerts: boolean
  foul_warnings: boolean
  milestone_alerts: boolean
  push_enabled: boolean
}

// Convert URL-safe base64 to Uint8Array for VAPID key
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/')

  const rawData = window.atob(base64)
  const outputArray = new Uint8Array(rawData.length)

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i)
  }

  return outputArray
}

export function usePushNotifications() {
  const [state, setState] = useState<PushState>({
    supported: false,
    permission: 'default',
    subscribed: false,
    loading: true,
    error: null,
  })

  const [vapidKey, setVapidKey] = useState<string | null>(null)

  // Check support and current state on mount
  useEffect(() => {
    async function init() {
      // Check browser support
      if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
        setState((s) => ({
          ...s,
          supported: false,
          loading: false,
          error: 'Push notifications not supported in this browser',
        }))
        return
      }

      // Get VAPID public key from server
      try {
        const vapidResponse = await apiGet<VapidResponse>('/alerts/vapid-public-key')
        if (!vapidResponse.configured || !vapidResponse.public_key) {
          setState((s) => ({
            ...s,
            supported: false,
            loading: false,
            error: 'Push notifications not configured on server',
          }))
          return
        }
        setVapidKey(vapidResponse.public_key)
      } catch (err) {
        setState((s) => ({
          ...s,
          supported: false,
          loading: false,
          error: 'Failed to get push configuration',
        }))
        return
      }

      // Check notification permission
      const permission = Notification.permission

      // Register service worker
      try {
        const registration = await navigator.serviceWorker.register('/sw.js', {
          scope: '/',
        })

        // Check if already subscribed
        const subscription = await registration.pushManager.getSubscription()

        setState({
          supported: true,
          permission,
          subscribed: !!subscription,
          loading: false,
          error: null,
        })
      } catch (err) {
        console.error('[Push] Service worker registration failed:', err)
        setState((s) => ({
          ...s,
          supported: true,
          permission,
          loading: false,
          error: 'Failed to register service worker',
        }))
      }
    }

    init()
  }, [])

  // Subscribe to push notifications
  const subscribe = useCallback(async () => {
    if (!vapidKey) {
      setState((s) => ({ ...s, error: 'VAPID key not available' }))
      return false
    }

    setState((s) => ({ ...s, loading: true, error: null }))

    try {
      // Request notification permission
      const permission = await Notification.requestPermission()

      if (permission !== 'granted') {
        setState((s) => ({
          ...s,
          permission,
          loading: false,
          error: 'Notification permission denied',
        }))
        return false
      }

      // Get service worker registration
      const registration = await navigator.serviceWorker.ready

      // Subscribe to push
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey),
      })

      // Send subscription to server
      const subscriptionJson = subscription.toJSON()
      await apiPostAuth('/alerts/subscribe', {
        push_enabled: true,
        subscription: {
          endpoint: subscriptionJson.endpoint,
          keys: subscriptionJson.keys,
        },
      })

      setState({
        supported: true,
        permission: 'granted',
        subscribed: true,
        loading: false,
        error: null,
      })

      return true
    } catch (err) {
      console.error('[Push] Subscription failed:', err)
      setState((s) => ({
        ...s,
        loading: false,
        error: err instanceof Error ? err.message : 'Failed to subscribe',
      }))
      return false
    }
  }, [vapidKey])

  // Unsubscribe from push notifications
  const unsubscribe = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: null }))

    try {
      const registration = await navigator.serviceWorker.ready
      const subscription = await registration.pushManager.getSubscription()

      if (subscription) {
        await subscription.unsubscribe()
      }

      // Tell server to disable push
      await apiPostAuth('/alerts/subscribe', {
        push_enabled: false,
        subscription: null,
      })

      setState((s) => ({
        ...s,
        subscribed: false,
        loading: false,
        error: null,
      }))

      return true
    } catch (err) {
      console.error('[Push] Unsubscribe failed:', err)
      setState((s) => ({
        ...s,
        loading: false,
        error: err instanceof Error ? err.message : 'Failed to unsubscribe',
      }))
      return false
    }
  }, [])

  // Toggle subscription
  const toggle = useCallback(async () => {
    if (state.subscribed) {
      return unsubscribe()
    } else {
      return subscribe()
    }
  }, [state.subscribed, subscribe, unsubscribe])

  return {
    ...state,
    subscribe,
    unsubscribe,
    toggle,
  }
}
